import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { TrafficLight, getOilStatus } from "@/components/TrafficLight";
import { PlusIcon, PencilSimpleIcon, TrashIcon, EyeIcon } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CarFormDialog } from "@/components/CarFormDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const CarsPage = () => {
  const { t, lang } = useLang();
  const [cars, setCars] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async () => {
    const { data } = await api.get("/cars");
    setCars(data);
  };
  useEffect(() => { load(); }, []);

  const handleSaved = async () => {
    setOpenForm(false);
    setEditing(null);
    await load();
  };

  const doDelete = async () => {
    try {
      await api.delete(`/cars/${deleteTarget.car_id}`);
      toast.success(t("deletedCar"));
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="space-y-8" data-testid="cars-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">/fleet</div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">{t("cars")}</h1>
        </div>
        <button
          data-testid="add-car-btn"
          onClick={() => { setEditing(null); setOpenForm(true); }}
          className="inline-flex items-center gap-2 bg-white text-black px-4 py-3 font-semibold hover:bg-zinc-100 transition-colors"
        >
          <PlusIcon size={16} weight="bold" />
          {t("addCar")}
        </button>
      </div>

      <div className="border border-white/10 bg-[#121212] overflow-x-auto">
        <table className="w-full data-table text-sm">
          <thead>
            <tr className="text-zinc-500 text-xs uppercase tracking-[0.2em]">
              <th className="text-start px-4 py-3">{t("plateNumber")}</th>
              <th className="text-start px-4 py-3">{t("make")}</th>
              <th className="text-start px-4 py-3">{t("model")}</th>
              <th className="text-start px-4 py-3">{t("currentKm")}</th>
              <th className="text-start px-4 py-3">{t("intervalKm")}</th>
              <th className="text-start px-4 py-3">{t("remainingKm")}</th>
              <th className="text-start px-4 py-3">{t("status")}</th>
              <th className="text-end px-4 py-3">{t("actions")}</th>
            </tr>
          </thead>
          <tbody>
            {cars.map((c) => {
              const st = getOilStatus(c);
              return (
                <tr key={c.car_id} className="hover:bg-[#1C1C1C] transition-colors" data-testid={`car-row-${c.car_id}`}>
                  <td className="px-4 py-3 font-semibold">{c.plate_number}</td>
                  <td className="px-4 py-3 text-zinc-300">{c.make}</td>
                  <td className="px-4 py-3 text-zinc-300">{c.model}</td>
                  <td className="px-4 py-3 text-zinc-300">{(c.current_km || 0).toLocaleString(lang === "ar" ? "ar-EG" : "en-US")}</td>
                  <td className="px-4 py-3 text-zinc-300">{(c.oil_change_interval_km || 0).toLocaleString()}</td>
                  <td className={`px-4 py-3 font-semibold ${st.level === "red" ? "text-[#FF3B30]" : st.level === "yellow" ? "text-[#FFEA00]" : "text-[#39FF14]"}`}>
                    {st.remaining <= 0 ? `-${Math.abs(st.remaining).toFixed(0)}` : st.remaining.toFixed(0)}
                  </td>
                  <td className="px-4 py-3"><TrafficLight status={st.level} size="sm" /></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 justify-end">
                      <Link
                        to={`/cars/${c.car_id}`}
                        data-testid={`view-car-${c.car_id}`}
                        className="p-2 border border-white/10 hover:bg-[#1C1C1C]"
                      >
                        <EyeIcon size={16} />
                      </Link>
                      <button
                        data-testid={`edit-car-${c.car_id}`}
                        onClick={() => { setEditing(c); setOpenForm(true); }}
                        className="p-2 border border-white/10 hover:bg-[#1C1C1C]"
                      >
                        <PencilSimpleIcon size={16} />
                      </button>
                      <button
                        data-testid={`delete-car-${c.car_id}`}
                        onClick={() => setDeleteTarget(c)}
                        className="p-2 border border-white/10 hover:bg-[#1C1C1C] text-[#FF3B30]"
                      >
                        <TrashIcon size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {cars.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-zinc-500">{t("firstTimeCta")}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <CarFormDialog
        open={openForm}
        onOpenChange={setOpenForm}
        editing={editing}
        onSaved={handleSaved}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-[#121212] border border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("deleteCar")}</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              {t("confirmDelete")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn" className="bg-transparent border border-white/10 text-white hover:bg-[#1C1C1C]">{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-confirm-btn" onClick={doDelete} className="bg-[#FF3B30] text-white hover:bg-[#E12E24]">
              {t("deleteCar")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
